import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Compass, Target } from "@phosphor-icons/react";

// Rotating rings; player halts each ring near its gold target marker, then submits.
export default function RingChallenge({ challenge, onSubmit, submitting }) {
  const n = challenge.ring_count || challenge.targets.length;
  const [angles, setAngles] = useState(Array(n).fill(0));
  const [locked, setLocked] = useState(Array(n).fill(false));
  const anglesRef = useRef(Array(n).fill(0));
  const raf = useRef(null);
  const last = useRef(null);
  const speeds = challenge.speeds || Array(n).fill(50);
  const dirs = useRef(speeds.map((_, i) => (i % 2 === 0 ? 1 : -1)));

  useEffect(() => {
    const tick = (t) => {
      if (last.current == null) last.current = t;
      const dt = (t - last.current) / 1000;
      last.current = t;
      const next = anglesRef.current.map((a, i) => {
        if (locked[i]) return a;
        let v = (a + dirs.current[i] * speeds[i] * dt) % 360;
        return v < 0 ? v + 360 : v;
      });
      anglesRef.current = next;
      setAngles([...next]);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  const lockRing = (i) => {
    if (locked[i] || submitting) return;
    const next = [...locked];
    next[i] = true;
    setLocked(next);
  };

  const allLocked = locked.every(Boolean);
  const submit = () => {
    cancelAnimationFrame(raf.current);
    onSubmit(anglesRef.current.map((a) => Number(a.toFixed(1))));
  };

  const radius = [78, 56, 34];

  return (
    <div className="mx-auto max-w-2xl">
      <div className="ff-panel rounded-md p-6 sm:p-8">
        <div className="mb-2 flex items-center gap-2 text-ff-gold">
          <Compass size={22} weight="duotone" />
          <span className="font-mono-ff text-xs uppercase tracking-widest">Ring Alignment · The Meridian</span>
        </div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Align the Astrolabe</h2>
        <p className="mt-2 text-sm text-ff-muted">Halt each rotating ring so its pointer meets the golden target, then commit the alignment.</p>

        <div className="relative mx-auto mt-6 h-56 w-56">
          <svg viewBox="0 0 200 200" className="h-full w-full">
            {angles.map((a, i) => {
              const R = radius[i] || 30;
              const tgt = challenge.targets[i];
              const rad = (deg) => ((deg - 90) * Math.PI) / 180;
              return (
                <g key={i}>
                  <circle cx="100" cy="100" r={R} fill="none" stroke="#B5A64244" strokeWidth="6" />
                  {/* target marker */}
                  <circle cx={100 + R * Math.cos(rad(tgt))} cy={100 + R * Math.sin(rad(tgt))} r="5" fill="#D4AF37" />
                  {/* pointer */}
                  <line x1="100" y1="100" x2={100 + R * Math.cos(rad(a))} y2={100 + R * Math.sin(rad(a))}
                    stroke={locked[i] ? "#2D5A27" : "#F2E8D3"} strokeWidth="3" strokeLinecap="round" />
                </g>
              );
            })}
            <circle cx="100" cy="100" r="4" fill="#D4AF37" />
          </svg>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {angles.map((_, i) => (
            <button key={i} onClick={() => lockRing(i)} disabled={locked[i] || submitting} data-testid={`ring-lock-${i}`}
              className={`rounded-sm border py-2 text-xs font-medium transition-colors ${locked[i] ? "border-ff-success/50 bg-ff-success/15 text-ff-success" : "border-ff-brass/40 text-ff-parchment hover:border-ff-gold"}`}>
              {locked[i] ? `Ring ${i + 1} locked` : `Lock Ring ${i + 1}`}
            </button>
          ))}
        </div>

        <motion.button whileTap={{ scale: 0.96 }} onClick={submit} disabled={!allLocked || submitting} data-testid="ring-submit-btn"
          className="mt-5 w-full rounded-sm bg-ff-gold py-4 text-lg font-bold text-ff-bg transition-colors hover:bg-ff-brass disabled:opacity-50">
          {submitting ? "..." : allLocked ? "Commit Alignment" : "Lock all three rings"}
        </motion.button>
      </div>
    </div>
  );
}
